import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { forwardRef, memo, useEffect, useRef, useState } from 'react';

import * as THREE from 'three';
import { GUI } from 'three/examples/jsm/libs/lil-gui.module.min.js';
import Stats from 'three/examples/jsm/libs/stats.module.js';
import { MarchingCubes } from 'three/examples/jsm/objects/MarchingCubes.js';
import { ToonShaderHatching } from 'three/examples/jsm/shaders/ToonShader.js';

import WebGLErrorBoundary, {
  isWebGLAvailable
} from 'src/shared/components/WebGLErrorBoundary';

/** R3F may use a different three.js copy than this module; use type flags, not instanceof. */
const isDirectionalLight = (
  child: THREE.Object3D
): child is THREE.DirectionalLight =>
  (child as THREE.DirectionalLight).isDirectionalLight === true;

const isAmbientLight = (child: THREE.Object3D): child is THREE.AmbientLight =>
  (child as THREE.AmbientLight).isAmbientLight === true;

interface BlobV2Props {
  className?: string;
  width?: number;
  height?: number;
  showStats?: boolean;
  showGUI?: boolean;
  speed?: number;
  numBlobs?: number;
  isolation?: number;
  lookAt?: {
    x: number;
    y: number;
    z: number;
  };
  zoom?: number;
  targetZoom?: number;
  onZoomComplete?: () => void;
}

// Helper function to detect device capabilities
const getOptimalResolution = (): number => {
  // Check for low-end devices
  const nav = navigator as Navigator & { deviceMemory?: number };
  const isLowEndDevice =
    navigator.hardwareConcurrency <= 4 ||
    (nav.deviceMemory && nav.deviceMemory <= 4) ||
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );

  // Reduce resolution for low-end devices
  return isLowEndDevice ? 22 : 30;
};

// Helper function to get optimal FPS based on device capabilities
const getOptimalFPS = (): number => {
  // Check for low-end devices (same logic as resolution)
  const nav = navigator as Navigator & { deviceMemory?: number };
  const isLowEndDevice =
    navigator.hardwareConcurrency <= 4 ||
    (nav.deviceMemory && nav.deviceMemory <= 4) ||
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );

  // Cap FPS at 30 for low-end devices, 60 for others
  return isLowEndDevice ? 30 : 60;
};

// Create hatching material with custom shader modifications
const createHatchingMaterial = (
  light: THREE.DirectionalLight,
  ambientLight: THREE.AmbientLight
): THREE.ShaderMaterial => {
  const baseColor = new THREE.Color(0x000eef); // #000EEF
  const hatchingShaderUniforms = THREE.UniformsUtils.clone(
    ToonShaderHatching.uniforms
  );

  hatchingShaderUniforms['uDirLightPos'] = { value: light.position };
  hatchingShaderUniforms['uDirLightColor'] = { value: light.color };
  hatchingShaderUniforms['uAmbientLightColor'] = {
    value: ambientLight.color
  };
  hatchingShaderUniforms['uColor'] = { value: baseColor }; // Base blue color
  hatchingShaderUniforms['uHatchingColor'] = { value: baseColor }; // Blue for hatching lines

  // Modify the fragment shader to apply colors
  const hatchingVertexShader = ToonShaderHatching.vertexShader;
  let hatchingFragmentShader = ToonShaderHatching.fragmentShader;

  // Add color uniform declarations at the top with other uniforms
  if (!hatchingFragmentShader.includes('uniform vec3 uColor')) {
    // Insert after the first uniform declaration or at the start of uniforms section
    const uniformSectionMatch =
      hatchingFragmentShader.match(/(uniform\s+[^;]+;)/);

    if (uniformSectionMatch) {
      hatchingFragmentShader = hatchingFragmentShader.replace(
        uniformSectionMatch[0],
        `${uniformSectionMatch[0]}\nuniform vec3 uColor;\nuniform vec3 uHatchingColor;`
      );
    } else {
      // If no uniform section found, add at the beginning
      hatchingFragmentShader = `uniform vec3 uColor;\nuniform vec3 uHatchingColor;\n${hatchingFragmentShader}`;
    }
  } else if (!hatchingFragmentShader.includes('uniform vec3 uHatchingColor')) {
    // Add hatching color if base color already exists
    hatchingFragmentShader = hatchingFragmentShader.replace(
      'uniform vec3 uColor;',
      'uniform vec3 uColor;\nuniform vec3 uHatchingColor;'
    );
  }

  // Modify the final color output to:
  // 1. Apply blue color to the base
  // 2. Replace all dark/black areas (hatching lines and shading) with red
  // Find the last gl_FragColor assignment and modify it
  const lastGlFragColorMatchHatching = hatchingFragmentShader.match(
    /gl_FragColor\s*=\s*[^;]+;/g
  );

  if (lastGlFragColorMatchHatching) {
    const lastAssignment =
      lastGlFragColorMatchHatching[lastGlFragColorMatchHatching.length - 1];

    // Replace with logic that applies blue to base and red to all dark areas
    hatchingFragmentShader = hatchingFragmentShader.replace(
      lastAssignment,
      `${lastAssignment}\n  // Store original color before modification\n  vec3 originalColor = gl_FragColor.rgb;\n  // Apply blue base color\n  gl_FragColor.rgb *= uColor;\n  // Calculate luminance to detect dark areas (hatching lines and shadows)\n  float luminance = dot(originalColor, vec3(0.299, 0.587, 0.114));\n  float darkness = 1.0 - luminance;\n  // Replace all dark areas (lines and shading) with red - more aggressive threshold\n  if (darkness > 0.1 || luminance < 0.5) {\n    // Mix red based on darkness, making all dark areas red\n    float redAmount = smoothstep(0.1, 0.9, darkness);\n    gl_FragColor.rgb = mix(gl_FragColor.rgb, uHatchingColor, redAmount * 0.95);\n  }\n  // Also apply red tint to shading areas\n  if (luminance < 0.7) {\n    gl_FragColor.rgb = mix(gl_FragColor.rgb, uHatchingColor, (0.7 - luminance) * 0.6);\n  }`
    );
  } else {
    // Fallback: append before the closing brace of main()
    hatchingFragmentShader = hatchingFragmentShader.replace(
      /(\s*}\s*)$/,
      '\n  // Store original color before modification\n  vec3 originalColor = gl_FragColor.rgb;\n  // Apply blue base color\n  gl_FragColor.rgb *= uColor;\n  // Calculate luminance to detect dark areas (hatching lines and shadows)\n  float luminance = dot(originalColor, vec3(0.299, 0.587, 0.114));\n  float darkness = 1.0 - luminance;\n  // Replace all dark areas (lines and shading) with red - more aggressive threshold\n  if (darkness > 0.1 || luminance < 0.5) {\n    // Mix red based on darkness, making all dark areas red\n    float redAmount = smoothstep(0.1, 0.9, darkness);\n    gl_FragColor.rgb = mix(gl_FragColor.rgb, uHatchingColor, redAmount * 0.95);\n  }\n  // Also apply red tint to shading areas\n  if (luminance < 0.7) {\n    gl_FragColor.rgb = mix(gl_FragColor.rgb, uHatchingColor, (0.7 - luminance) * 0.6);\n  }\n$1'
    );
  }

  return new THREE.ShaderMaterial({
    uniforms: hatchingShaderUniforms,
    vertexShader: hatchingVertexShader,
    fragmentShader: hatchingFragmentShader
  });
};

// Update cubes function
const updateCubes = (object: MarchingCubes, time: number, numblobs: number) => {
  object.reset();

  const subtract = 12;
  const strength = 1.2 / ((Math.sqrt(numblobs) - 1) / 4 + 1);

  for (let i = 0; i < numblobs; i++) {
    const ballx =
      Math.sin(i + 1.26 * time * (1.03 + 0.5 * Math.cos(0.21 * i))) * 0.27 +
      0.5;
    const bally =
      Math.abs(Math.cos(i + 1.12 * time * Math.cos(1.22 + 0.1424 * i))) * 0.77;
    const ballz =
      Math.cos(i + 1.32 * time * 0.1 * Math.sin(0.92 + 0.53 * i)) * 0.27 + 0.5;

    object.addBall(ballx, bally, ballz, strength, subtract);
  }

  object.update();
};

// Blob mesh component that handles MarchingCubes
interface BlobMeshProps {
  speed: number;
  numBlobs: number;
  isolation: number;
  lookAt: { x: number; y: number; z: number };
  zoom: number;
  targetZoom?: number;
  onZoomComplete?: () => void;
  effectControllerRef: React.MutableRefObject<{
    speed: number;
    numBlobs: number;
    resolution: number;
    isolation: number;
  }>;
  resolutionRef: React.MutableRefObject<number>;
  currentZoomRef: React.MutableRefObject<number>;
  targetZoomRef: React.MutableRefObject<number>;
  zoomCompleteCallbackFiredRef: React.MutableRefObject<boolean>;
  isVisibleRef: React.MutableRefObject<boolean>;
  isTabVisibleRef: React.MutableRefObject<boolean>;
  statsRef: React.MutableRefObject<Stats | null>;
  showStats: boolean;
}

const BlobMesh = memo(
  ({
    speed,
    numBlobs,
    isolation,
    lookAt,
    zoom,
    targetZoom,
    onZoomComplete,
    effectControllerRef,
    resolutionRef,
    currentZoomRef,
    targetZoomRef,
    zoomCompleteCallbackFiredRef,
    isVisibleRef,
    isTabVisibleRef,
    statsRef,
    showStats
  }: BlobMeshProps) => {
    const { camera, scene, gl } = useThree();
    const effectRef = useRef<MarchingCubes | null>(null);
    const materialRef = useRef<THREE.ShaderMaterial | null>(null);
    const timeRef = useRef<number>(0);
    const lastFrameTimeRef = useRef<number>(0);
    const targetFPS = getOptimalFPS();
    const frameInterval = 1000 / targetFPS; // ~33.33ms for 30 FPS, ~16.67ms for 60 FPS
    const [contextRestoreKey, setContextRestoreKey] = useState(0);

    // Set scene background and camera lookAt
    useEffect(() => {
      scene.background = null; // Transparent background
      camera.lookAt(lookAt.x, lookAt.y, lookAt.z);
    }, [scene, camera, lookAt]);

    // WebGL context recovery handlers
    useEffect(() => {
      if (!gl?.domElement) return;

      const handleContextLost = (event: Event) => {
        event.preventDefault();
        console.warn('WebGL context lost, attempting recovery...');
      };

      const handleContextRestored = () => {
        console.log('WebGL context restored, reinitializing...');
        // Clean up old resources
        if (effectRef.current && scene) {
          scene.remove(effectRef.current);
          effectRef.current = null;
        }
        if (materialRef.current) {
          materialRef.current.dispose();
          materialRef.current = null;
        }
        // Force re-initialization by updating state
        setContextRestoreKey((prev) => prev + 1);
      };

      const canvas = gl.domElement;

      canvas.addEventListener('webglcontextlost', handleContextLost);
      canvas.addEventListener('webglcontextrestored', handleContextRestored);

      return () => {
        canvas.removeEventListener('webglcontextlost', handleContextLost);
        canvas.removeEventListener(
          'webglcontextrestored',
          handleContextRestored
        );
      };
    }, [gl, scene]);

    // Initialize MarchingCubes
    useEffect(() => {
      let cleanup: (() => void) | undefined;
      let rafId: number;

      // Clean up previous effect and material before creating new ones
      const previousEffect = effectRef.current;
      const previousMaterial = materialRef.current;

      if (previousEffect && scene) {
        scene.remove(previousEffect);
        // Dispose MarchingCubes geometry if it exists
        if ('geometry' in previousEffect && previousEffect.geometry) {
          const geometry = previousEffect.geometry as THREE.BufferGeometry;

          if (geometry.dispose) {
            geometry.dispose();
          }
        }
      }
      if (previousMaterial) {
        previousMaterial.dispose();
      }

      // Wait a frame to ensure lights are in the scene
      rafId = requestAnimationFrame(() => {
        // Get lights from scene
        const light = scene.children.find(isDirectionalLight);
        const ambientLight = scene.children.find(isAmbientLight);

        if (!light || !ambientLight) return;

        // Create material
        const material = createHatchingMaterial(light, ambientLight);

        materialRef.current = material;

        // Create MarchingCubes
        const resolution = resolutionRef.current;
        const effect = new MarchingCubes(
          resolution,
          material,
          true,
          true,
          100000
        );

        effect.position.set(0, 0, 0);
        effect.scale.set(700, 700, 700);
        effect.enableUvs = false;
        effect.enableColors = false;
        effect.isolation = isolation;
        effectRef.current = effect;
        scene.add(effect);

        // Initialize zoom
        currentZoomRef.current = zoom;
        targetZoomRef.current = zoom;
        camera.position.set(-500 * zoom, 1200 * zoom, 600 * zoom);
        camera.lookAt(lookAt.x, lookAt.y, lookAt.z);

        cleanup = () => {
          if (effect && scene) {
            scene.remove(effect);
            // Dispose MarchingCubes geometry if it exists
            // MarchingCubes creates internal geometry that needs cleanup
            if ('geometry' in effect && effect.geometry) {
              const geometry = effect.geometry as THREE.BufferGeometry;

              if (geometry.dispose) {
                geometry.dispose();
              }
            }
          }
          if (material) {
            material.dispose();
          }
        };
      });

      return () => {
        cancelAnimationFrame(rafId);
        if (cleanup) {
          cleanup();
        }
      };
    }, [
      scene,
      camera,
      isolation,
      zoom,
      lookAt,
      resolutionRef,
      currentZoomRef,
      targetZoomRef,
      contextRestoreKey
    ]);

    // Update controller when props change
    useEffect(() => {
      effectControllerRef.current.speed = speed;
      effectControllerRef.current.numBlobs = numBlobs;
      effectControllerRef.current.isolation = isolation;
    }, [speed, numBlobs, isolation, effectControllerRef]);

    // Update target zoom when prop changes
    useEffect(() => {
      if (targetZoom !== undefined) {
        targetZoomRef.current = targetZoom;
        zoomCompleteCallbackFiredRef.current = false;
      }
    }, [targetZoom, targetZoomRef, zoomCompleteCallbackFiredRef]);

    // Animation loop using useFrame
    useFrame((_state, delta) => {
      // FPS limiting: Cap at 60 FPS
      const now = performance.now();
      const elapsed = now - lastFrameTimeRef.current;

      if (elapsed < frameInterval) {
        // Skip this frame if not enough time has passed
        return;
      }

      lastFrameTimeRef.current = now - (elapsed % frameInterval); // Maintain frame timing accuracy

      // Skip rendering if tab is not visible or component is not visible
      if (!isTabVisibleRef.current || !isVisibleRef.current) {
        // Still update time to prevent animation jump when resuming
        timeRef.current += delta * effectControllerRef.current.speed * 0.5;

        return;
      }

      const effect = effectRef.current;
      const controller = effectControllerRef.current;

      if (!effect) return;

      // Update time
      timeRef.current += delta * controller.speed * 0.5;

      // Smooth camera zoom animation
      if (
        targetZoomRef.current !== undefined &&
        targetZoomRef.current !== currentZoomRef.current
      ) {
        const zoomSpeed = 0.01;
        const zoomDelta = delta * zoomSpeed;
        const diff = targetZoomRef.current - currentZoomRef.current;

        if (Math.abs(diff) < 0.01) {
          currentZoomRef.current = targetZoomRef.current;
          if (onZoomComplete && !zoomCompleteCallbackFiredRef.current) {
            zoomCompleteCallbackFiredRef.current = true;
            onZoomComplete();
          }
        } else {
          currentZoomRef.current += diff * zoomDelta;
        }

        camera.position.set(
          -500 * currentZoomRef.current,
          1200 * currentZoomRef.current,
          600 * currentZoomRef.current
        );
        camera.lookAt(lookAt.x, lookAt.y, lookAt.z);
      }

      // Update resolution if changed
      if (controller.resolution !== resolutionRef.current) {
        resolutionRef.current = controller.resolution;
        effect.init(Math.floor(resolutionRef.current));
      }

      // Update isolation if changed
      if (controller.isolation !== effect.isolation) {
        effect.isolation = controller.isolation;
      }

      // Update cubes
      updateCubes(effect, timeRef.current, controller.numBlobs);

      // Update stats
      if (showStats && statsRef.current) {
        statsRef.current.update();
      }
    });

    return null; // MarchingCubes is added directly to scene
  }
);

BlobMesh.displayName = 'BlobMesh';

const BlobV2 = memo(
  forwardRef<HTMLDivElement, BlobV2Props>(
    (
      {
        className,
        width,
        height,
        showStats = false,
        showGUI = false,
        speed = 0.03,
        numBlobs = 12,
        isolation = 55,
        lookAt = { x: 0, y: 0, z: 0 },
        zoom = 1.0,
        targetZoom,
        onZoomComplete
      },
      ref
    ) => {
      const containerRef = useRef<HTMLDivElement | null>(null);
      const resolutionRef = useRef<number>(getOptimalResolution());
      const statsRef = useRef<Stats | null>(null);
      const guiRef = useRef<GUI | null>(null);
      const isVisibleRef = useRef<boolean>(true);
      const isTabVisibleRef = useRef<boolean>(true);
      const effectControllerRef = useRef<{
        speed: number;
        numBlobs: number;
        resolution: number;
        isolation: number;
      }>({
        speed: speed,
        numBlobs: numBlobs,
        resolution: getOptimalResolution(),
        isolation: isolation
      });
      const currentZoomRef = useRef<number>(zoom);
      const targetZoomRef = useRef<number>(zoom);
      const zoomCompleteCallbackFiredRef = useRef<boolean>(false);

      // Merge refs: use forwarded ref if provided, otherwise use internal ref
      const setRef = (node: HTMLDivElement | null) => {
        containerRef.current = node;
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref) {
          (ref as any).current = node;
        }
      };

      // Setup stats and GUI
      useEffect(() => {
        const container = containerRef.current;

        if (!container) return;

        // STATS
        if (showStats) {
          const stats = new Stats();

          statsRef.current = stats;
          container.appendChild(stats.dom);
        }

        // GUI
        if (showGUI) {
          const gui = new GUI();

          guiRef.current = gui;

          // Simulation folder
          const simulationFolder = gui.addFolder('Simulation');

          simulationFolder.add(
            effectControllerRef.current,
            'speed',
            0.01,
            8.0,
            0.01
          );
          simulationFolder.add(
            effectControllerRef.current,
            'numBlobs',
            1,
            50,
            1
          );
          simulationFolder.add(
            effectControllerRef.current,
            'resolution',
            14,
            100,
            1
          );
          simulationFolder.add(
            effectControllerRef.current,
            'isolation',
            10,
            300,
            1
          );
        }

        // Intersection Observer to detect visibility
        const intersectionObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              isVisibleRef.current = entry.isIntersecting;
            });
          },
          {
            threshold: 0.1 // Trigger when at least 10% is visible
          }
        );

        if (container) {
          intersectionObserver.observe(container);
        }

        // Visibility API to detect tab visibility
        const handleVisibilityChange = () => {
          isTabVisibleRef.current = !document.hidden;
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        isTabVisibleRef.current = !document.hidden;

        return () => {
          document.removeEventListener(
            'visibilitychange',
            handleVisibilityChange
          );
          if (container) {
            intersectionObserver.unobserve(container);
          }
          intersectionObserver.disconnect();

          if (guiRef.current) {
            guiRef.current.destroy();
          }

          if (statsRef.current && container.contains(statsRef.current.dom)) {
            container.removeChild(statsRef.current.dom);
            statsRef.current = null;
          }
        };
      }, [showStats, showGUI]);

      if (!isWebGLAvailable())
        return <div ref={setRef} className={className} />;

      return (
        <div
          ref={setRef}
          className={className}
          style={
            width && height
              ? { width, height }
              : { width: '100%', height: '100%' }
          }
        >
          <WebGLErrorBoundary>
            <Canvas
              camera={{
                fov: 45,
                near: 1,
                far: 10000,
                position: [-500 * zoom, 1200 * zoom, 600 * zoom]
              }}
              gl={{ alpha: true, antialias: true }}
              style={{ width: '100%', height: '100%' }}
              dpr={window.devicePixelRatio}
            >
              <ambientLight intensity={3} color={0x323232} />
              <directionalLight
                intensity={3}
                color={0xffffff}
                position={[0.5, 0.5, 1]}
              />
              <pointLight
                intensity={3}
                color={0xff7c00}
                position={[0, 0, 100]}
                distance={0}
                decay={0}
              />
              <BlobMesh
                speed={speed}
                numBlobs={numBlobs}
                isolation={isolation}
                lookAt={lookAt}
                zoom={zoom}
                targetZoom={targetZoom}
                onZoomComplete={onZoomComplete}
                effectControllerRef={effectControllerRef}
                resolutionRef={resolutionRef}
                currentZoomRef={currentZoomRef}
                targetZoomRef={targetZoomRef}
                zoomCompleteCallbackFiredRef={zoomCompleteCallbackFiredRef}
                isVisibleRef={isVisibleRef}
                isTabVisibleRef={isTabVisibleRef}
                statsRef={statsRef}
                showStats={showStats}
              />
            </Canvas>
          </WebGLErrorBoundary>
        </div>
      );
    }
  ),
  // Custom comparison function to prevent unnecessary re-renders
  (prevProps, nextProps) => {
    return (
      prevProps.className === nextProps.className &&
      prevProps.width === nextProps.width &&
      prevProps.height === nextProps.height &&
      prevProps.showStats === nextProps.showStats &&
      prevProps.showGUI === nextProps.showGUI &&
      prevProps.speed === nextProps.speed &&
      prevProps.numBlobs === nextProps.numBlobs &&
      prevProps.isolation === nextProps.isolation &&
      prevProps.zoom === nextProps.zoom &&
      prevProps.lookAt?.x === nextProps.lookAt?.x &&
      prevProps.lookAt?.y === nextProps.lookAt?.y &&
      prevProps.lookAt?.z === nextProps.lookAt?.z &&
      prevProps.targetZoom === nextProps.targetZoom
    );
  }
);

BlobV2.displayName = 'BlobV2';

export default BlobV2;
